import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'reactstrap';
import adminService from '../../../services/adminService';
import { ExternalApiLog, ExternalApiLogFilter } from '../../../services/types/ExternalApiLog';
import {
  Button,
  Modal,
  PageHeader,
  Section,
  SpinnerArea,
  TablePagination,
} from '../../../shared/components';
import Table, { Cell } from '../../../shared/components/Table';
import ToggleButton from '../../../shared/components/ToggleButton';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import { dateTimeFormat } from '../../../shared/misc/utils';
import MainContext from '../../App/MainProvider/context';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import reducer from './reducer';
import Role from '../../../types/role';

const ExternalApiLogs: React.FC = () => {
  const { t } = useTranslation();
  const { service, notify, user } = useContext(MainContext);
  const [loading, setLoading] = useState(false);
  const [includeSuccessFilter, setIncludeSuccessFilter] = useState(false);
  const [showErrorMessageModal, setShowErrorMessageModal] = useState(false);
  const [currentErrorMessage, setCurrentErrorMessage] = useState('');
  const [selectedRow, setSelectedRow] = useState<ExternalApiLog | null>(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  const initFilter: ExternalApiLogFilter = {
    pageNumber: 1,
    pageSize: 10,
    includeSuccess: includeSuccessFilter,
  };

  const [data, , filter, dispatch, , reload] = useServiceReducer(
    adminService.getExternalApiLogs,
    reducer,
    initFilter
  );

  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });
  const changePageSize = (size: number) => dispatch({ type: 'PAGE_SIZE', value: size });

  const changeIncludeSuccess = (includeSuccess: boolean) => {
    setIncludeSuccessFilter(includeSuccess);
    dispatch({ type: 'FILTER', includeSuccess });
  };

  const retry = (apiCallId: number) => {
    service(() => adminService.retryExternalApiLog(apiCallId), {
      loader: setLoading,
      callback: () => {
        notify(t('api-calls'), t('resubmited'), 'success');
        reload();
      },
    });
  };

  const deleteApiCall = (apiCallId: number) => {
    service(() => adminService.deleteExternalApiLog(apiCallId), {
      loader: setLoading,
      callback: () => {
        setSelectedRow(null);
        setConfirmDeleteVisible(false);
        notify(t('api-calls'), t('successDelete'), 'success');
        reload();
      },
    });
  };

  const openDeleteConfirmation = (obj: ExternalApiLog) => {
    setConfirmDeleteVisible(true);
    setSelectedRow(obj);
  };

  const renderActions = (v: string, obj: ExternalApiLog) => {
    const buttons = [];
    if (v !== null && v.toLowerCase() === 'error') {
      buttons.push(
        <Button
          htmlId={`resubmit_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="arrow-circle-up"
          text=""
          onClick={() => retry(obj.id)}
          title={t('resubmit')}
        />
      );
    }

    if (v !== null && v.toLowerCase() === 'error' && user.roleCode === Role.Admin) {
      buttons.push(
        <Button
          htmlId={`delete_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="trash-alt"
          text=""
          onClick={() => openDeleteConfirmation(obj)}
          title={t('delete')}
        />
      );
    }

    return buttons.length > 0 ? <>{buttons}</> : <>-</>;
  };

  const showErrorMessage = (errorMessage: string) => {
    setCurrentErrorMessage(errorMessage);
    setShowErrorMessageModal(true);
  };

  const cells: Cell<ExternalApiLog>[] = [
    {
      name: 'operationNumber',
      column: 'operationNumber',
      sortDisabled: true,
      value: (x) => x.operationNumber,
    },
    {
      name: 'interface',
      column: 'interface',
      sortDisabled: true,
      value: (x) => x.interface,
    },
    {
      name: 'source',
      column: 'source',
      sortDisabled: true,
      value: (x) => x.source,
    },
    {
      name: 'documentName',
      column: 'documentUrl',
      sortDisabled: true,
      value: (x) => x.documentName || '-',
    },
    {
      name: 'startDate',
      column: 'startDate',
      sortDisabled: true,
      value: (x) => (dateTimeFormat(x.startDate) || t('na')) as string,
    },
    {
      name: 'endDate',
      column: 'endDate',
      sortDisabled: true,
      value: (x) => (x.endDate ? dateTimeFormat(x.endDate) : t('na')) as string,
    },
    {
      name: 'status',
      column: 'status',
      sortDisabled: true,
      value: (x) => x.status,
      render: (v) => (
        <div className={`badge-base api-call-status ${v.toLowerCase()}`}>
          <div className="badge-text">
            <span>{v}</span>
          </div>
        </div>
      ),
    },
    {
      name: 'errorMessage',
      column: 'errorMessage',
      value: (x) => x.errorMessage || '',
      sortDisabled: true,
      render: (v, obj) =>
        v !== null && v !== '' ? (
          <Button
            htmlId={`error_message_button_${obj.id}`}
            className="btn-tertiary no-padding"
            icon="clipboard-list"
            text=""
            onClick={() => showErrorMessage(v)}
          />
        ) : (
          <>-</>
        ),
    },
    {
      column: t('actions'),
      value: (x) => x.status,
      sortDisabled: true,
      render: renderActions,
    },
  ];

  return (
    <>
      <Container className="dashboard">
        <PageHeader title={t('apiLogs')} noBack />
        <Row>
          <Section>
            <div>
              <div className="details-bar">
                <ToggleButton
                  htmlId="switch_includeSuccess"
                  checked={includeSuccessFilter}
                  toggleAction={changeIncludeSuccess}
                />
                <span className="ml-2 successfulDocumentsSent">{t('successfulDocumentsSent')}</span>
              </div>
              <SpinnerArea loading={loading}>
                {data && (
                  <>
                    <Table data={data.list} cells={cells} keyFunc={(x) => x.id} />

                    <TablePagination
                      pageSize={filter.pageSize}
                      pageNumber={filter.pageNumber}
                      total={data.total}
                      onChangePage={changePage}
                      onChangeSize={changePageSize}
                    />
                  </>
                )}
              </SpinnerArea>
            </div>
          </Section>
        </Row>
      </Container>
      <ConfirmModal
        title={t('delete')}
        buttonText={t('delete')}
        visible={confirmDeleteVisible}
        setVisible={() => setConfirmDeleteVisible(false)}
        action={() => {
          if (selectedRow) {
            deleteApiCall(selectedRow.id);
          }
        }}
      >
        {t('deleteApiLogConfirmationMsg')}
      </ConfirmModal>
      <Modal
        title={t('errorMessage')}
        visible={showErrorMessageModal}
        setVisible={() => setShowErrorMessageModal(false)}
        actions={[{ text: 'Close', action: () => setShowErrorMessageModal(false) }]}
        customClass="extra_wide-modal api-logs-error-modal"
        onClosed={() => setShowErrorMessageModal(false)}
      >
        <div className="history-content flow-history mb-0 table-responsive">
          <table>
            <tbody>
              <tr>
                <td className="api-error-message">{currentErrorMessage}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
};

export default ExternalApiLogs;
